/**
 * Périodes de formation approximatives (mois entiers, format "YYYY-MM").
 *
 * Quand le client ne connaît pas encore ses dates exactes, la demande porte des
 * mois plutôt que des jours : « Janvier / Février 2027 ». Ces périodes vivent
 * dans `quotes.dates_periodes`, à côté de `quotes.dates_disponibilites` (jours
 * précis). Les deux sont exclusifs : renseigner l'un vide l'autre.
 *
 * Shared entre le serveur (affichage du devis) et le client (aperçu immédiat
 * dans l'éditeur) — une seule source de vérité pour le format affiché.
 */

export const PERIOD_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

export const MONTH_LABELS_FR = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

/**
 * Ne garde que les périodes au format "YYYY-MM", dédoublonnées et triées.
 * @param {unknown} periods
 * @returns {string[]}
 */
export function normalizePeriods(periods) {
  if (!Array.isArray(periods)) return [];
  return [...new Set(periods.filter((p) => typeof p === 'string' && PERIOD_PATTERN.test(p)))].sort();
}

/**
 * Décompose une période : "2027-01" → { year: 2027, month: 1 }.
 * @param {unknown} period
 * @returns {{ year: number, month: number }|null} null si le format est invalide
 */
export function parsePeriod(period) {
  if (typeof period !== 'string' || !PERIOD_PATTERN.test(period)) return null;
  const [year, month] = period.split('-');
  return { year: Number(year), month: Number(month) };
}

/**
 * Libellé d'une période seule : "2027-01" → "Janvier 2027".
 * @param {string} period
 * @returns {string}
 */
export function formatPeriodLabel(period) {
  const parsed = parsePeriod(period);
  if (!parsed) return '';
  return `${MONTH_LABELS_FR[parsed.month - 1]} ${parsed.year}`;
}

/**
 * Libellé d'une liste de périodes, regroupé par année :
 *   ["2027-01", "2027-02"]            → "Janvier / Février 2027"
 *   ["2026-12", "2027-01"]            → "Décembre 2026, Janvier 2027"
 * @param {unknown} periods
 * @returns {string}
 */
export function formatPeriodsDisplay(periods) {
  const normalized = normalizePeriods(periods);
  if (normalized.length === 0) return '';

  const byYear = new Map();
  for (const period of normalized) {
    const { year, month } = parsePeriod(period);
    if (!byYear.has(year)) byYear.set(year, []);
    byYear.get(year).push(MONTH_LABELS_FR[month - 1]);
  }

  return [...byYear.entries()]
    .map(([year, months]) => `${months.join(' / ')} ${year}`)
    .join(', ');
}

/**
 * Libellé d'une liste de jours précis :
 *   ["2027-03-12", "2027-03-13"] → "Le 12/03/2027, le 13/03/2027"
 * Format historique du devis (PDF) : partagé avec l'aperçu immédiat de l'éditeur
 * pour que les deux produisent exactement la même chaîne.
 * @param {unknown} dates
 * @returns {string} chaîne vide si aucun jour exploitable
 */
export function formatDatesDisponibilitesDisplay(dates) {
  if (!Array.isArray(dates) || dates.length === 0) return '';
  const formatted = dates
    .map((dateStr) => `le ${new Date(dateStr).toLocaleDateString('fr-FR')}`)
    .join(', ');
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/**
 * Applique l'exclusivité jours précis / périodes / dates inconnues au niveau des
 * données (et pas seulement dans l'UI) : dès que les dates sont inconnues ou que
 * des jours précis sont posés, les périodes approximatives sont vidées.
 * @param {Object} input
 * @param {unknown} input.dates - Jours précis (dates_disponibilites)
 * @param {unknown} input.periodes - Périodes approximatives (dates_periodes)
 * @param {unknown} input.inconnues - Dates inconnues (dates_inconnues)
 * @returns {{ dates: unknown[], periodes: string[], inconnues: boolean }}
 */
export function resolveExclusiveDates({ dates, periodes, inconnues } = {}) {
  const resolvedDates = Array.isArray(dates) ? dates : [];
  const resolvedInconnues = Boolean(inconnues);
  return {
    dates: resolvedDates,
    periodes:
      resolvedInconnues || resolvedDates.length > 0 ? [] : normalizePeriods(periodes),
    inconnues: resolvedInconnues,
  };
}
